import {CF} from 'module/CustomField/util/CustomField';
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {CustomFieldTO, SisType, StTagTO} from 'generated/JavaTO'
import _ from 'lodash';
import {CFMember, CFModel, CFValue, isCFMember} from 'module/CustomField/type/CFType'
import {getSisDurationAsNumber, getSisDurationBetween24H, parseSisTime, SisTime} from 'util/SisDateTime'
import {Booking, Persurl} from 'generated/JavaTOmodifications'
import {CFDefModelInitContext} from 'module/CustomField/util/getCFDefModelInitContextObservable'
import {pasteClipboardFromCFAttr, PastePhase} from 'module/Clipboard/util/pasteClipboardFromCFAttr'
import {cfAttrTypeIsNumberOrStringComparator} from 'module/CustomField/util/cfAttrTypeIsNumberOrStringComparator'
import {getGroupedCFMissingNullValues} from 'module/CustomField/util/getGroupedCFMissingNullValues'

function _addCFMemberOncePerDstField(cf: CFMember[], mem: CFMember) {
	if (cf.find(x => x.dstField === mem.dstField && x.pos === mem.pos)) return;
	cf.push(mem)
}

/** Do only use after removing a cf-Namespace Member to maintain correct cf-Member amount */
export function _ensureCFMemberMinAmount<T extends CFModel | Booking | Persurl, P extends CFModel | Booking | Persurl>(cfDefs: CustomFieldTO[], model: T, persistedModel?: P | null) {
	if (!model.cf) model.cf = []
	const cf = model.cf
	cfDefs = cfDefs.filter(cfDef => CF.isCFNamespace(cfDef) && !CF.isLabelField(cfDef))
	const groupedCFDefs = _.groupBy(cfDefs.filter(c => c.cfGroupId != null), 'cfGroupId')
	const singleCFDefs = cfDefs.filter(c => c.cfGroupId == null)
	const groupedCF = _.groupBy(cf, 'dstField')
	singleCFDefs.forEach(cfDef => {
		const dstField = CF.getCFNamespaceField(cfDef)
		const existing = groupedCF[dstField] || []
		let addAmount = cfDef.minAmount - existing.length
		if (addAmount > 0) {
			const persistedPositions = persistedModel?.cf?.flatMap(x => x.dstField === dstField ? [x.pos] : []) || []
			const lastPos = _.max([...existing.map(x => x.pos), ...persistedPositions])
			let nextPos = !lastPos && lastPos !== 0 ? 0 : lastPos + 1
			while (addAmount-- > 0) {
				_addCFMemberOncePerDstField(cf, {dstField, pos: nextPos, data: null})
				nextPos++
			}
		}
	})
	for (let cfGroupId in groupedCFDefs) {
		const cfDefs = groupedCFDefs[cfGroupId] || []
		const dstFieldCFDefMap = _.keyBy(cfDefs, CF.getCFNamespaceField)
		const minAmount = _.max(cfDefs.map(x => x.minAmount)) || 0
		const dstFields = Object.keys(dstFieldCFDefMap)
		const byPos = _.groupBy(dstFields.flatMap(dstField => groupedCF[dstField] || []), 'pos')
		for (let k in byPos) {
			// fill up holes in group
			const pos = parseInt(k)
			const values = byPos[pos] || []
			dstFields
				.filter(dstField => !values.some(v => v.dstField === dstField))
				.forEach(dstField => _addCFMemberOncePerDstField(cf, {
					dstField,
					pos,
					data: null,
				}))
		}
		const allPos = Object.keys(byPos).map(k => parseInt(k))
		let addAmount = minAmount - allPos.length
		if (addAmount > 0) {
			const persistedPositions = persistedModel?.cf?.flatMap(x => dstFields.includes(x.dstField) ? [x.pos] : []) || []
			const lastPos = _.max([...allPos, ...persistedPositions])
			let nextPos = !lastPos && lastPos !== 0 ? 0 : lastPos + 1
			while (addAmount-- > 0) {
				for (let dstField in dstFieldCFDefMap) {
					_addCFMemberOncePerDstField(cf, {dstField, pos: nextPos, data: null})
				}
				nextPos++
			}
		}
	}
}

function setInitValue<T extends CFModel>(cfDef: CustomFieldTO, model: T, overrideDefaultValue?: CFValue | CFValue[]) {
	const defaultValue = overrideDefaultValue == null ? cfDef.defaultValue : overrideDefaultValue
	const isArrayDefaultValue = Array.isArray(defaultValue)
	if (CF.isCFNamespace(cfDef)) {
		if (!defaultValue && defaultValue !== 0) return;
		let dvArray = !isArrayDefaultValue ? [defaultValue] : defaultValue
		let dvLen = dvArray.length
		let pos = 0
		if (dvLen < cfDef.minAmount) {
			// ensure all values are set when only one value is provided, but minAmount is higher
			dvArray = Array.from({length: cfDef.minAmount}, (_, i) => dvArray[i % dvLen])
			dvLen = dvArray.length
		}
		for (let i = 0; i <= cfDef.minAmount; i++) {
			if (pos >= dvLen) break;
			if (CF.isNull(cfDef, CF.getValueFrom(cfDef, model, {pos}) as CFValue)) {
				CF.setValueTo(cfDef, model, dvArray[pos], {pos})
			}
			pos++
		}
	} else {
		const currVal = CF.getValueFrom(cfDef, model) as CFValue
		if (CF.isNull(cfDef, currVal)) {
			// only set, when not already set in model
			const dv = isArrayDefaultValue ? defaultValue[0] : defaultValue
			if (dv !== null) {
				CF.setValueTo(cfDef, model, dv);
			} else {
				CF.setValueTo(cfDef, model, null);
			}
		}
	}
}

function setUrlParamsToModel<T extends CFModel>(cfDefs: CustomFieldTO[], urlParams: Partial<T> | undefined, model: T) {
	if (!urlParams) return;
	cfDefs.forEach(cfDef => {
		if (!CF.isCFNamespace(cfDef)) {
			let value = urlParams[cfDef.dstField] as CFValue;
			if (value && CF.isNullOrDefault(cfDef, model)) CF.setValueTo(cfDef, model, value);
		} else {
			let {cf} = urlParams;
			if (cf) {
				let dstField = CF.getCFNamespaceField(cfDef);
				(cf as CFMember[]).filter(cv => cv.dstField === dstField)
					.forEach(cfMember => {
						if ((cfMember && (cfMember.pos || cfMember.pos === 0)) && CF.isNullOrDefault(cfDef, model, cfMember.pos)) {
							CF.setValueTo(cfDef, model, cfMember)
						}
					});
			}
		}
	})
}

function prepareInitValueArray(override: CFValue | CFValue[]) {
	if (Array.isArray(override)) {
		if (override.length === 0) return null
		if (override.length === 1) return override[0]
	}
	return override
}

type setInitValueCb<T extends CFModel> = (cfDef: CustomFieldTO, model: T, initValue: CFValue | CFValue[]) => void

function getDepDstField(cf: CustomFieldTO) {
	return CF.getCFAttr(cf, CFAttrKey.TimeFromAttr)?.value as string | undefined
}

function getSortedCFDefsByFieldDependency(cfDefs: CustomFieldTO[]) {
	// sort cfdefs by dependency, that the init values are written in the right order
	const cfByDstField = _.groupBy(cfDefs, 'dstField')
	return [...cfDefs].sort((a, b) => {
		const aDep = getDepDstField(a)
		const bDep = getDepDstField(b)
		const aLevel = aDep && cfByDstField[aDep] ? 1 : 0
		const bLevel = bDep && cfByDstField[bDep] ? 1 : 0

		if (aLevel !== bLevel) {
			return aLevel - bLevel
		}
		return a.id - b.id
	})
}

function setInitValues<T extends CFModel>(cfDefs: CustomFieldTO[], context: CFDefModelInitContext, model: T, setInitValue: setInitValueCb<T>) {
	cfDefs = getSortedCFDefsByFieldDependency(cfDefs)
	cfDefs.forEach(cfDef => {
		let override: CFValue | CFValue[] = cfDef.cfAttrs.filter(a => a.key === CFAttrKey.initValue)
			.sort(cfAttrTypeIsNumberOrStringComparator)
			.map(x => x.value)
		override = prepareInitValueArray(override)
		let kontStammBewEinh
		if (!override && override !== 0 && (kontStammBewEinh = context.kontStamm?.bewEinh?.trim()) && CF.getCFAttr(cfDef, CFAttrKey.initFromStammKost)?.value) {
			override = prepareInitValueArray(kontStammBewEinh)
		}
		type KT = keyof StTagTO | undefined
		let initFromStTag: KT
		if (!override && override !== 0 && context.stTag && (initFromStTag = CF.getCFAttr(cfDef, CFAttrKey.initValueFromStTag)?.value as KT)) {
			override = context.stTag[initFromStTag]
			if (override && cfDef.type === SisType.Duration && /(^.*(Beg|Ende)$)|(^(beg|ende).*$)/.test(initFromStTag)) {
				// convert to duration. value from st_tag is point in time, but used in zsp field
				const timeFromAttr = CF.getCFAttr(cfDef, CFAttrKey.TimeFromAttr)?.value as keyof T | undefined
				if (timeFromAttr) {
					const timeFrom = model[timeFromAttr] as SisTime || 0
					const d = getSisDurationBetween24H(parseSisTime(timeFrom, {ignoreSeconds: true}), override)
					override = getSisDurationAsNumber(d)
				}
			}
			override = prepareInitValueArray(override)
		}
		if (!override && override !== 0 && CF.getCFAttr(cfDef, CFAttrKey.initValueFromDq)?.value) {
			override = prepareInitValueArray(context.initValueFromDqMap?.[cfDef.id])
		}
		// has to be set immediatly, because init values may rely on previous init values (i.e. sttag init by timeFrom and timeTo: timeTo requires timeFrom to be set -> only in this order allowed
		setInitValue(cfDef, model, override)
	})
}

function ensureGroupedCFValuesPresent<T extends CFModel>(cfDefs: CustomFieldTO[], model: T) {
	getGroupedCFMissingNullValues(cfDefs, model).forEach(({cfDef, pos}) => {
		CF.setValueTo(cfDef, model, null, {pos})
	})
}
export function initCFDefModel<T extends CFModel>(cfDefs: CustomFieldTO[], model: T, persistedModel?: T | null, context: CFDefModelInitContext = {}): T {
	if (!cfDefs) return model

	model = _.cloneDeep(model)

	_ensureCFMemberMinAmount(cfDefs, model, persistedModel)

	if (persistedModel) return model;

	const {clipboard, urlParams} = context;
	//clipboard first
	if (clipboard) {
		pasteClipboardFromCFAttr(PastePhase.INIT, cfDefs, clipboard, model, false)
			.forEach(({cfDef, model, value, options}) => {
				CF.setValueTo(cfDef, model, value, options)
			})
	}
	setUrlParamsToModel(cfDefs, urlParams, model)
	// other init values last
	setInitValues(cfDefs, context, model, setInitValue)
	//ensure cf groups are fully initialized and empty fields are set to null
	ensureGroupedCFValuesPresent(cfDefs, model)
	return model
}

function addIfNullOrDefault(cfDef: CustomFieldTO, model: CFModel, value: CFMember | CFValue, pos?: number) {
	if (CF.isNullOrDefault(cfDef, model, pos)) {
		const positions = CF.getPositions([cfDef], model)
		if (positions.length < cfDef.maxAmount || positions.includes(pos)) {
			CF.setValueTo(cfDef, model, value, {pos})
		}
	}
}

function setCFMemberInitValue<T extends CFModel>(cfDef: CustomFieldTO, model: T, initValue: CFValue | CFValue[], pos: number) {
	let v: CFValue | CFMember = null
	if (Array.isArray(initValue)) {
		const availablePos = new Set(initValue.map((v, i) => isCFMember(v) ? v.pos : i))
		if (availablePos.size === 1) {
			v = initValue.find((v, i) => isCFMember(v) ? v.dstField === cfDef.dstField : i === 0)
		} else {
			v = initValue.find((v, i) => isCFMember(v) ? v.pos === pos && v.dstField === cfDef.dstField : i === pos)
		}
	} else {
		v = initValue
	}
	addIfNullOrDefault(cfDef, model, v, pos)
}

export function addInitializedCFValues<T extends CFModel>(cfDefs: CustomFieldTO[], model: T, pos: number, context: CFDefModelInitContext = {}) {
	if (!cfDefs || !model) return undefined

	model = _.cloneDeep(model)
	const {clipboard, urlParams} = context; // urlParams not in use (no use case atm)
	if (clipboard) {
		//ensure cf member is added before clipboard. clipboard is only applied to present cf members. null values are not interfering with initValues after clipboard.
		cfDefs.forEach(cfDef => {
			const positions = CF.getPositions([cfDef], model)
			if (positions.length < cfDef.maxAmount && !positions.includes(pos)) {
				CF.setValueTo(cfDef, model, null, {pos})
			}
		})
		const clipboardContent = pasteClipboardFromCFAttr(PastePhase.ADD_CF, cfDefs, clipboard, model, false)
			.map(({cfDef, model, value, options}) => ({cfDef, model, value, pos: options?.pos}))
		let c: typeof clipboardContent;
		if (new Set(clipboardContent.map(({pos}) => pos)).size !== 1) {
			c = clipboardContent.filter(x => x.pos === pos)
		} else {
			c = clipboardContent
		}
		c.forEach(({cfDef, model, value}) => CF.setValueTo(cfDef, model, value, {pos}))
	}
	// setUrlParamsToModel(cfDefs, urlParams, model) // url params only on first init / not afterwards when editing
	// other init values last
	setInitValues(cfDefs, context, model, (cfDef, model, initValue) => setCFMemberInitValue(cfDef, model, initValue, pos))
	//ensure cf groups are fully initialized and empty fields are set to null
	ensureGroupedCFValuesPresent(cfDefs, model)

	return model.cf
}